import {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	useRef,
	type ReactNode,
} from "react";
import { authApi, proApi, type User } from "./api";
import { ApiError } from "./api/base";
import { queryClient } from "./query-client";

type AuthState = {
	user: User | null;
	isLoading: boolean;
	isAuthenticated: boolean;
	isAdmin: boolean;
	isSuperAdmin: boolean;
	hasProAccess: boolean;
};

type AuthContextType = AuthState & {
	signIn: (
		email: string,
		password: string,
		options?: { skipStateUpdate?: boolean },
	) => Promise<{
		isAdmin: boolean;
		isSuperAdmin: boolean;
		hasProAccess: boolean;
		pendingInvitationToken?: string;
	}>;
	signUp: (email: string, password: string, name: string, termsAccepted?: boolean) => Promise<void>;
	signOut: () => Promise<void>;
	refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
	const [state, setState] = useState<AuthState>({
		user: null,
		isLoading: true,
		isAuthenticated: false,
		isAdmin: false,
		isSuperAdmin: false,
		hasProAccess: false,
	});

	const refreshSession = useCallback(async () => {
		try {
			const session = await authApi.getSession();
			if (session?.user) {
				// Fetch roles and pro in parallel (instead of serial)
				let isAdmin = false;
				let isSuperAdmin = false;
				let hasProAccess = false;

				const [rolesResult, proResult] = await Promise.allSettled([
					proApi.getMyRoles(),
					proApi.getMyPro(),
				]);

				// If both calls failed with 401, session is stale — treat as logged out
				const is401 = (r: PromiseSettledResult<unknown>) =>
					r.status === "rejected" && r.reason instanceof ApiError && r.reason.status === 401;
				if (is401(rolesResult) && is401(proResult)) {
					setState({
						user: null,
						isLoading: false,
						isAuthenticated: false,
						isAdmin: false,
						isSuperAdmin: false,
						hasProAccess: false,
					});
					return;
				}

				if (rolesResult.status === "fulfilled") {
					isAdmin = rolesResult.value.data?.isAdmin ?? false;
					isSuperAdmin = rolesResult.value.data?.isSuperAdmin ?? false;
				}
				if (proResult.status === "fulfilled") {
					hasProAccess = !!proResult.value.data?.proId;
				}

				setState({
					user: session.user,
					isLoading: false,
					isAuthenticated: true,
					isAdmin,
					isSuperAdmin,
					hasProAccess,
				});
			} else {
				setState({
					user: null,
					isLoading: false,
					isAuthenticated: false,
					isAdmin: false,
					isSuperAdmin: false,
					hasProAccess: false,
				});
			}
		} catch {
			setState({
				user: null,
				isLoading: false,
				isAuthenticated: false,
				isAdmin: false,
				isSuperAdmin: false,
				hasProAccess: false,
			});
		}
	}, []);

	useEffect(() => {
		refreshSession();
	}, [refreshSession]);

	// Listen for 401 events dispatched by the query client
	useEffect(() => {
		const guestPaths = ["/login", "/register", "/forgot-password", "/reset-password", "/verify-email", "/accept-invitation", "/auth/sign-in", "/auth/sign-up", "/auth/forgot-password", "/auth/reset-password"];
		const handleSessionExpired = () => {
			queryClient.clear();
			setState({
				user: null,
				isLoading: false,
				isAuthenticated: false,
				isAdmin: false,
				isSuperAdmin: false,
				hasProAccess: false,
			});
			// Only redirect if not already on a guest page (prevents reload loop)
			const isGuestPage = guestPaths.some((p) => window.location.pathname.startsWith(p));
			if (!isGuestPage) {
				window.location.href = "/login";
			}
		};

		window.addEventListener("auth:session-expired", handleSessionExpired);
		return () =>
			window.removeEventListener("auth:session-expired", handleSessionExpired);
	}, []);

	// Cross-tab session detection: revalidate session when tab becomes visible
	const isValidatingRef = useRef(false);
	useEffect(() => {
		const handleVisibilityChange = async () => {
			if (
				document.visibilityState === "visible" &&
				state.isAuthenticated &&
				!isValidatingRef.current
			) {
				isValidatingRef.current = true;
				const controller = new AbortController();
				const timeout = setTimeout(() => controller.abort(), 5000);
				try {
					const sessionPromise = authApi.getSession();
					// Race the session check against the abort signal
					const abortPromise = new Promise<null>((_, reject) => {
						controller.signal.addEventListener("abort", () =>
							reject(new DOMException("Aborted", "AbortError")),
						);
					});
					const session = await Promise.race([sessionPromise, abortPromise]);
					if (!session?.user) {
						// Session expired or logged out from another tab
						setState({
							user: null,
							isLoading: false,
							isAuthenticated: false,
							isAdmin: false,
							isSuperAdmin: false,
							hasProAccess: false,
						});
						window.location.href = "/login";
					}
				} catch (err) {
					// On timeout (AbortError), don't log out — just skip refresh
					if (err instanceof DOMException && err.name === "AbortError") {
						// Timeout — skip validation silently
					} else {
						// Session check failed — treat as expired
						setState({
							user: null,
							isLoading: false,
							isAuthenticated: false,
							isAdmin: false,
							isSuperAdmin: false,
							hasProAccess: false,
						});
						window.location.href = "/login";
					}
				} finally {
					clearTimeout(timeout);
					isValidatingRef.current = false;
				}
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () =>
			document.removeEventListener("visibilitychange", handleVisibilityChange);
	}, [state.isAuthenticated]);

	const signIn = async (
		email: string,
		password: string,
		options?: { skipStateUpdate?: boolean },
	) => {
		await authApi.signIn(email, password);

		// When skipStateUpdate is true, just authenticate without updating React state.
		// The caller handles navigation and state refresh (e.g., invitation flow).
		if (options?.skipStateUpdate) {
			return { isAdmin: false, isSuperAdmin: false, hasProAccess: false };
		}

		// Fetch session, then roles + pro in parallel
		const session = await authApi.getSession();
		let isAdmin = false;
		let isSuperAdmin = false;
		let hasProAccess = false;
		let pendingInvitationToken: string | undefined;

		if (session?.user) {
			const [rolesResult, proResult] = await Promise.allSettled([
				proApi.getMyRoles(),
				proApi.getMyPro(),
			]);

			if (rolesResult.status === "fulfilled") {
				isAdmin = rolesResult.value.data?.isAdmin ?? false;
				isSuperAdmin = rolesResult.value.data?.isSuperAdmin ?? false;
			}
			if (proResult.status === "fulfilled") {
				hasProAccess = !!proResult.value.data?.proId;
				pendingInvitationToken = proResult.value.data?.pendingInvitation?.token;
			}

			setState({
				user: session.user,
				isLoading: false,
				isAuthenticated: true,
				isAdmin,
				isSuperAdmin,
				hasProAccess,
			});
		}

		return { isAdmin, isSuperAdmin, hasProAccess, pendingInvitationToken };
	};

	const signUp = async (email: string, password: string, name: string, termsAccepted?: boolean) => {
		// Note: We don't set isLoading here because that would cause GuestGuard
		// to show a spinner, which unmounts the RegisterPage and loses its state.
		// The register page manages its own loading state instead.
		await authApi.signUp(email, password, name, termsAccepted);
	};

	const signOut = async () => {
		setState((s) => ({ ...s, isLoading: true }));
		try {
			await authApi.signOut();
		} finally {
			queryClient.clear();
			setState({
				user: null,
				isLoading: false,
				isAuthenticated: false,
				isAdmin: false,
				isSuperAdmin: false,
				hasProAccess: false,
			});
		}
	};

	return (
		<AuthContext.Provider
			value={{
				...state,
				signIn,
				signUp,
				signOut,
				refreshSession,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
